import React, { useEffect, useState } from 'react';
import { fetchServices, createService, updateService, deleteService } from '../services/serviceService';
import { Button, Input, TextArea, Toast } from '../components/common';
import AdminLayout from './AdminLayout';

const ManageServices = () => {
    const [services, setServices] = useState([]);
    const [formData, setFormData] = useState({ title: '', description: '', order: 0 });
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [status, setStatus] = useState({ type: '', msg: '' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadServices();
    }, []);

    const loadServices = async () => {
        try {
            const { data } = await fetchServices();
            setServices(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
        } catch (error) {
            console.error('Error fetching services', error);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editMode) {
                await updateService(editId, formData);
                setStatus({ type: 'success', msg: 'Service updated successfully!' });
            } else {
                await createService(formData);
                setStatus({ type: 'success', msg: 'Service created successfully!' });
            }
            setFormData({ title: '', description: '', order: 0 });
            setEditMode(false);
            setEditId(null);
            loadServices();
        } catch (error) {
            setStatus({ type: 'error', msg: 'Operation failed. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const editHandler = (service) => {
        setFormData({ title: service.title, description: service.description, order: service.order || 0 });
        setEditMode(true);
        setEditId(service._id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const deleteHandler = async (id) => {
        if (window.confirm('Are you sure you want to delete this service?')) {
            try {
                await deleteService(id);
                setStatus({ type: 'success', msg: 'Service deleted successfully!' });
                loadServices();
            } catch (error) {
                setStatus({ type: 'error', msg: 'Failed to delete service.' });
            }
        }
    };

    const cancelEdit = () => {
        setFormData({ title: '', description: '', order: 0 });
        setEditMode(false);
        setEditId(null);
    };

    return (
        <AdminLayout title="Manage Services">
            <Toast
                type={status.type}
                message={status.msg}
                onClose={() => setStatus({ type: '', msg: '' })}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6" id="admin-root">
                {/* Form Column */}
                <div className="lg:col-span-1">
                    <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6 sticky top-6">
                        <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-2">
                            <h3 className="text-lg font-semibold text-white">
                                {editMode ? 'Edit Service' : 'Add New Service'}
                            </h3>
                            {editMode && (
                                <button onClick={cancelEdit} className="text-xs text-gray-400 hover:text-white">
                                    Cancel
                                </button>
                            )}
                        </div>

                        <form onSubmit={submitHandler}>
                            <Input
                                label="Service Title"
                                name="title"
                                required
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="e.g. Full-Stack Web App Development"
                            />

                            <Input
                                label="Display Order"
                                name="order"
                                type="number"
                                value={formData.order}
                                onChange={handleChange}
                                placeholder="0"
                            />

                            <TextArea
                                label="Description"
                                name="description"
                                required
                                rows={4}
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Detailed description of the engineering service offering..."
                            />

                            <Button
                                type="submit"
                                variant={editMode ? 'warning' : 'primary'}
                                fullWidth
                                isLoading={loading}
                            >
                                {editMode ? 'Update Service' : 'Create Service'}
                            </Button>
                        </form>
                    </div>
                </div>

                {/* Services List Column */}
                <div className="lg:col-span-2">
                    <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-700 bg-gray-900/50 flex justify-between items-center">
                            <h3 className="text-lg font-medium text-white">Existing Services</h3>
                            <span className="bg-gray-700 text-gray-300 py-1 px-3 rounded-full text-xs font-bold">
                                {services.length}
                            </span>
                        </div>

                        <div className="divide-y divide-gray-700">
                            {services.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    No services added yet.
                                </div>
                            ) : (
                                services.map((service, index) => (
                                    <div key={service._id} className="p-6 hover:bg-gray-700/30 transition-colors flex items-start justify-between">
                                        <div className="flex items-start gap-4">
                                            <div className="text-indigo-400 font-bold text-lg">
                                                {String(index + 1).padStart(2, '0')}.
                                            </div>
                                            <div>
                                                <h4 className="text-white font-semibold text-base mb-1">{service.title}</h4>
                                                <p className="text-gray-400 text-sm">{service.description}</p>
                                                <span className="inline-block mt-2 text-xs text-gray-500">
                                                    Display Order: {service.order || 0}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 ms-4">
                                            <button
                                                onClick={() => editHandler(service)}
                                                className="text-indigo-400 hover:text-indigo-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all"
                                                title="Edit Service"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                onClick={() => deleteHandler(service._id)}
                                                className="text-red-400 hover:text-red-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all"
                                                title="Delete Service"
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ManageServices;
